import React, { useEffect, useRef, useState } from 'react';
import { SiteFooter } from '@/components/landing/SiteFooter';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Home, Printer, ChevronLeft, Users } from 'lucide-react';
import { LibraryBracket } from '@/components/bracket/custom/LibraryBracket';
import { MyShares } from '@/components/bracket/custom/MyShares';
import { FORMATS, TEMPLATES, templateById } from '@/lib/bracket/templates';
import { fetchBracketPreview } from '@/lib/bracket/shareApi';

const FILTERS = [['all', 'All Formats'], ['single_elimination', 'Single Elimination'], ['double_elimination', 'Double Elimination'], ['consolation', '3-Game Guarantee']];

const TemplateCard = ({ t, selected, onClick }) => {
  const f = FORMATS[t.format];
  return (
    <Card className={`border-slate-200 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] ${selected ? 'ring-2 ring-[#00247D]' : ''}`}
      onClick={onClick} data-testid={`template-${t.id}`}>
      <CardContent className="p-4 text-center">
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${f.color} text-white`}><Users className="w-6 h-6" /></div>
        <h3 className="text-lg font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>{t.numTeams} TEAMS</h3>
        <Badge className={`mt-1 ${f.color} text-white text-[10px]`}>{f.label}</Badge>
      </CardContent>
    </Card>
  );
};

export default function BracketLibrary() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const boardRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [formatFilter, setFormatFilter] = useState('all');
  const [entry, setEntry] = useState({});

  const filtered = formatFilter === 'all' ? TEMPLATES : TEMPLATES.filter((t) => t.format === formatFilter);

  const loadPreview = async (template) => {
    setSelected(template.id);
    setLoadingPreview(true);
    try {
      setPreview(await fetchBracketPreview(template.format, template.numTeams));
    } catch { setPreview(null); } finally { setLoadingPreview(false); }
  };

  // ?template=… reopens a bracket (back from upgrading or signing in); &share= edits a shared one.
  useEffect(() => {
    const t = templateById(params.get('template'));
    if (t) {
      setEntry({ share: params.get('share') || '', openShare: params.get('action') === 'share' });
      loadPreview(t);
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const editShare = (s) => {
    const t = templateById(s.template_id);
    if (!t) return;
    setEntry({ share: s.id });
    loadPreview(t);
  };

  const fmt = preview && FORMATS[preview.format];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-600 px-2 sm:px-4" data-testid="library-home-btn"><Home className="w-5 h-5" /></Button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-[#00247D] leading-tight" style={{ fontFamily: 'Barlow Condensed' }}>BRACKET LIBRARY</h1>
                <p className="text-xs text-slate-500 truncate">Printable tournament bracket templates · 4 to 64 teams</p>
              </div>
            </div>
            {preview && !loadingPreview && (
              <Button variant="outline" className="shrink-0" data-testid="print-library-btn" onClick={() => boardRef.current?.run('print')}>
                <Printer className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Print Bracket</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-6">
        {!preview ? (
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-6">
              {FILTERS.map(([key, label]) => (
                <Button key={key} variant={formatFilter === key ? 'default' : 'outline'} size="sm"
                  className={formatFilter === key ? 'bg-[#00247D] hover:bg-[#001A5C]' : ''}
                  onClick={() => setFormatFilter(key)} data-testid={`filter-${key}`}>{label}</Button>
              ))}
            </div>
            <MyShares onEdit={editShare} />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filtered.map((t) => <TemplateCard key={t.id} t={t} selected={selected === t.id} onClick={() => { setEntry({}); loadPreview(t); }} />)}
            </div>
          </div>
        ) : (
          <div>
            <Button variant="outline" className="mb-4" onClick={() => { setPreview(null); setSelected(null); setEntry({}); }} data-testid="library-back-btn">
              <ChevronLeft className="w-4 h-4 mr-2" />Back to Library
            </Button>
            {loadingPreview || !preview ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <LibraryBracket key={`${selected}:${entry.share || ''}`} ref={boardRef} template={{ id: selected }} preview={preview} fmt={fmt}
                initialShareId={entry.share} openShare={entry.openShare} />
            )}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
